import { Box } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';

import { StepFooter } from './StepFooter';
import { SteppedModalHeader } from './SteppedModalHeader';
import type { SteppedModalProps } from './types';

/** Fade transition shared by all step swaps */
const fadeVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const fadeTransition = { duration: 0.15, ease: 'easeInOut' as const };

/**
 * Presentational shell for multi-step modal flows. Owns the branding header,
 * crossfade transitions between steps, dot indicators, and the back/next footer.
 *
 * The consuming feature supplies the active step content (`children`), the dot
 * count/index, the per-step footer config, and a `contentKey` that changes
 * whenever the visible step changes (drives the crossfade). The feature keeps
 * its own step state machine — the shell is purely chrome.
 */
export const SteppedModal: React.FC<SteppedModalProps> = ({
  branding,
  onClose,
  totalSteps,
  currentStep,
  footer,
  contentKey,
  contentRef,
  children,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh', overflow: 'hidden' }}>
      {/* Persistent header — never shrinks */}
      <Box sx={{ flexShrink: 0 }}>
        <SteppedModalHeader branding={branding} onClose={onClose} />
      </Box>

      {/* Scrollable content — header/footer stay fixed; content scrolls when it
          exceeds the available height. minHeight:0 lets this flex child shrink
          below its content size so overflow can take effect. */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {/* layout animates height changes smoothly between steps */}
        <motion.div layout transition={{ duration: 0.25, ease: 'easeInOut' }}>
          <AnimatePresence mode="wait">
            <motion.div
              ref={contentRef}
              key={contentKey}
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={fadeTransition}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </Box>

      {/* Unified footer — never shrinks, always visible */}
      <Box sx={{ flexShrink: 0 }}>
        <StepFooter
          currentStep={currentStep}
          totalSteps={totalSteps}
          onBack={footer.onBack}
          onNext={footer.onNext}
          nextLabel={footer.nextLabel}
          nextDisabled={footer.nextDisabled}
          showBack={footer.showBack}
          showNext={footer.showNext}
        />
      </Box>
    </Box>
  );
};
